import * as React from "react";
import { useSelector, useDispatch } from "react-redux";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { Card, Grid, Menu, Tab, TabProps } from "semantic-ui-react";

import { Authorize } from "@teamprevent/common/reauthorize";

import { ADMIN_ROLES } from "@teamprevent/common/auth/roles";

import { EmployeeRecordDetailModel, AppState } from "../model";
import { basePathSelector, routeIdSelector } from "../selectors";
import { ApplicationState } from "../../../applicationState";
import { useEmployeeRecordDetailsModel } from "../hooks";

import { coreDetailsTab, documentsTab } from "./details";
import { WarningIndicator } from "./details/WarningIndicator";

import { Link, push } from "redux-little-router";

export interface CoreDetailProps {
    employeeRecord: EmployeeRecordDetailModel;
}

export interface DetailProps extends CoreDetailProps {
    path: string;
    basePath: string;
    tab?: string;
    customerId: string;
    employeeRecordId: string;
}

const tabs = [coreDetailsTab, documentsTab];

const currentTab = (state: AppState) => state.router.pathname!.substring(
    basePathSelector(state).length + 1 + routeIdSelector(state).toString().length);

export const Detail = () => {
    const dispatch = useDispatch();
    const enabledEmployeeAuditLogs = getFeatureToggles()[FeatureToggleNames.EnableEmployeeAuditLogs];
    const tab = useSelector(currentTab);
    const basePath = useSelector(basePathSelector);
    const employeeRecordId = useSelector((state: ApplicationState) => state.router.params!.employeeRecordId);
    const customerId = useSelector((state: ApplicationState) => state.router.params!.customerId);
    const { employeeRecord, isLoaded: isEmployeeLoaded } = useEmployeeRecordDetailsModel({
        employeeRecordId,
        customerId
    });
    const handleTabChange = React.useCallback((_event: any, { activeIndex }: TabProps) => {
        dispatch(push(`${basePath}/${employeeRecordId}${tabs[activeIndex as unknown as number].path}`, {}));
    }, [basePath, dispatch, employeeRecordId]);

    const panes = tabs;
    const activeIndex = panes.slice(1).findIndex(p => tab && tab.startsWith(p.path)) + 1;

    const content = () => panes.length === 1
        ? <Card fluid>{panes[0].render()}</Card>
        : <Tab panes={panes} onTabChange={handleTabChange} activeIndex={activeIndex} />;

    return (
        <Grid container className="nomargintop">
            <Grid.Row>
                <Grid.Column>
                    {isEmployeeLoaded && <h1>
                        {employeeRecord.firstName} {employeeRecord.lastName} - {employeeRecord.personalReferenceNumber}
                        <WarningIndicator notes={employeeRecord.criticalNotes} />
                    </h1>}
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={3} as={Grid} padded>
                    <Grid.Column vertical as={Menu}>
                        <Menu.Item name="EmployeeRecords" as={Link} href={basePath}>
                            All Employee Records
                        </Menu.Item>
                        <Menu.Item name="OhCase" as={Link} href={`${basePath}/${employeeRecordId}/ohCases`}>
                            Employee Cases
                        </Menu.Item>
                        <Menu.Item name="GPSpecialistInfo" as={Link} href={`${basePath}/${employeeRecordId}/gpSpecialistInfo`}>
                            GP/Specialist info
                        </Menu.Item>
                        <Menu.Item name="Immunisations" as={Link} href={`${basePath}/${employeeRecordId}/immunisations`}>
                            Immunisations
                        </Menu.Item>
                        <Menu.Item name="OccupationalIllness" as={Link} href={`${basePath}/${employeeRecordId}/occupationalIllness`}>
                            Occupational Illness Record
                        </Menu.Item>
                        <Menu.Item name="ClinicalHistory" as={Link} href={`${basePath}/${employeeRecordId}/clinicalHistory`}>
                            Clinical history
                        </Menu.Item>
                        <Menu.Item
                            name="HealthSurveillanceRecalls"
                            as={Link}
                            href={`${basePath}/${employeeRecordId}/healthSurveillanceRecalls`}
                        >
                            HS / FFW Schedule
                        </Menu.Item>
                        {enabledEmployeeAuditLogs &&
                            <Authorize authorize={[ADMIN_ROLES.SYSTEM_ADMIN.name]}>
                                <Menu.Item name="EmployeeRecordAuditLog" as={Link} href={`${basePath}/${employeeRecordId}/auditLog`}>
                                    Employee Record Audit
                                </Menu.Item>
                            </Authorize>
                        }
                    </Grid.Column>
                </Grid.Column>
                <Grid.Column width={13}>
                    {content()}
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};
